import { jsxs, jsx } from "react/jsx-runtime";
import { css } from "@/styled-system/css";
import { token } from "@/styled-system/tokens";
import { Box, Slider } from "@zillow/constellation-10";
import { debounce } from "lodash";
import PropTypes from "prop-types";
import { useEffect, useMemo } from "react";
import { handleFieldChangeEvent } from "./DTICalculator.clickstream.esm.js";
const trackHeightStyles = css({
  height: "8px"
});
function getTrackGradient(debtsOut, mortgagePayment, monthlyIncome) {
  if (monthlyIncome <= 0) return void 0;
  const debtsPercent = debtsOut / monthlyIncome * 100;
  const totalPercent = (debtsOut + mortgagePayment) / monthlyIncome * 100;
  return `linear-gradient(to right, ${token.var("colors.orange.500")} 0% ${debtsPercent}%, ${token.var("colors.blue.900")} ${debtsPercent}% ${totalPercent}%, ${token.var("colors.blue.600")} ${totalPercent}% 100%)`;
}
const DTISlider = ({
  monthlyIncome,
  debtsOut,
  mortgagePayment,
  inputValue,
  onSliderChange
}) => {
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = '[data-c11n-component="Tooltip.ContentPositioner"], [data-c11n-component="Tooltip.Content"] { display: none !important; visibility: hidden !important; }';
    document.head.appendChild(style);
    return () => style.remove();
  }, []);
  const onSliderChangeDebounced = useMemo(() => debounce((value) => {
    onSliderChange(value);
    handleFieldChangeEvent("Debt slider", "slider");
  }, 50), [onSliderChange]);
  const trackBackground = getTrackGradient(debtsOut, mortgagePayment, monthlyIncome);
  const handlePercent = monthlyIncome > 0 ? (debtsOut + mortgagePayment) / monthlyIncome * 100 : 0;
  return /* @__PURE__ */ jsxs(Box, { css: {
    position: "relative",
    paddingTop: "layout.looser",
    overflow: "visible",
    paddingBottom: "tighter"
  }, children: [
    /* @__PURE__ */ jsx(Box, { style: {
      position: "absolute",
      left: `${handlePercent}%`,
      transform: "translateX(-50%)",
      top: "0"
    }, css: {
      display: "inline-flex",
      padding: "tighter",
      backgroundColor: "bg.neutral",
      borderWidth: "1px",
      borderStyle: "solid",
      borderColor: "border.muted",
      borderRadius: "obj.default",
      boxShadow: "sm",
      whiteSpace: "nowrap",
      textStyle: "body-sm"
    }, children: "$" + inputValue + "/mo" }),
    /* @__PURE__ */ jsxs(Slider.Root, { max: monthlyIncome, value: [debtsOut, mortgagePayment + debtsOut], onValueChange: onSliderChangeDebounced, snapDragDisabled: true, step: 10, css: {
      touchAction: "pan-y"
    }, children: [
      /* @__PURE__ */ jsx(Slider.Track, { className: trackHeightStyles, style: {
        background: trackBackground
      }, children: /* @__PURE__ */ jsx(Slider.ActiveTrack, { css: {
        backgroundColor: "transparent"
      } }) }),
      /* @__PURE__ */ jsx(Slider.Handle, { css: {
        visibility: "hidden"
      }, "aria-hidden": "true", tabIndex: -1 }),
      /* @__PURE__ */ jsx(Slider.Handle, { css: {
        top: "2px"
      }, ariaLabel: "total monthly payment", ariaValuetext: `$${inputValue} per month` })
    ] })
  ] });
};
DTISlider.propTypes = {
  monthlyIncome: PropTypes.number,
  debtsOut: PropTypes.number,
  mortgagePayment: PropTypes.number,
  inputValue: PropTypes.number,
  onSliderChange: PropTypes.func.isRequired
};
export {
  DTISlider as default
};
